import { AssetListCard } from '@atlas/ui-domain'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { BrandPage } from '@/domain/explore.types'
import { useBrandFiles } from '@/hooks/useExplore'
import { useThumbnails } from '@/hooks/useThumbnails'
import { downloadAssets } from '@/lib/download-asset'
import { toFileTypeRoles } from '@/lib/file-type-roles'
import type { FileWithTagsDTO } from '@/services/file.service'
import { BrandLinksCard } from './BrandLinksCard'
import type { BrandMetaLink } from './brand-meta'
import { BRAND_ASSETS_WORDS } from './brand-page-words'
import { ProductNamingCard } from './ProductNamingCard'

export interface BrandIdentityTabProps {
	brandCode: string
	/** The brand's external collections, from `brand.meta.links`. */
	links: BrandMetaLink[]
	/**
	 * The market the page is scoped to, or null when unscoped. Narrows the asset list to what
	 * APPLIES in that market — see {@link useBrandFiles}.
	 */
	marketCode: string | null
	/**
	 * Scopes the brand is called something else in, as the page read projects them.
	 *
	 * Passed down rather than read here: they arrive with the brand-page composition, so
	 * fetching them again for the tab would be a second request for data already in hand.
	 */
	names: BrandPage['names']
	/** The brand's own name — the baseline the market overrides differ from. */
	brandName: string
}

/**
 * How many files may be thumbnailed at once on this tab.
 *
 * The brand read is deliberately unfiltered ({@link useBrandFiles}), so unlike the product
 * page's kit there is no `file_type` narrowing to bound it before the thumbnails: a brand with
 * a few hundred image deliverables would otherwise open a presigned-URL read per file on tab
 * arrival, and re-poll every one of them every 4s for a minute whenever realtime is down.
 *
 * Past the budget a tile keeps its file-type marker, which is what a thumbnail that never
 * resolves shows anyway - so the cap costs recognisability on the long tail, not correctness.
 */
const THUMBNAIL_BUDGET = 120

/**
 * The prefix of `files` that may be thumbnailed, memoised on identity so the slice does not
 * hand {@link useThumbnails} a fresh array each render.
 */
function useThumbnailBudget(files: FileWithTagsDTO[]): FileWithTagsDTO[] {
	return useMemo(() => (files.length > THUMBNAIL_BUDGET ? files.slice(0, THUMBNAIL_BUDGET) : files), [files])
}

/**
 * What the brand LOOKS like: every file classified to it, grouped by the role each one plays,
 * beside the brand's contacts and collections.
 *
 * The read is unscoped by market on purpose ({@link useBrandFiles}) - this page holds a market
 * code but no applicability ladder, so a file classified above or beside that market cannot be
 * judged here. The list therefore states the brand's inventory; the product page states what
 * applies in a market.
 *
 * Its own component rather than inline in `BrandPage`, so the read fires when the tab is
 * selected rather than on arrival - an inactive tab panel is not mounted.
 */
export function BrandIdentityTab({ brandCode, links, names, marketCode, brandName }: BrandIdentityTabProps) {
	const navigate = useNavigate()
	const { data: files = [], isLoading, isError } = useBrandFiles(brandCode, marketCode)
	const thumbnails = useThumbnails(useThumbnailBudget(files))

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
			<div className="space-y-4 lg:col-span-2">
				<AssetListCard
					{...BRAND_ASSETS_WORDS}
					roles={toFileTypeRoles(files, thumbnails)}
					onOpenAsset={(assetId) => void navigate(`/assets/${assetId}`)}
					onDownloadAssets={downloadAssets}
					isLoading={isLoading}
					isError={isError}
				/>
			</div>
			<div className="space-y-4">
				{/* The same card the product page uses, with the brand's own wording: a brand's
				    names are region-split more often than a product's (Banamine/Finadyne). */}
				<ProductNamingCard
					names={names}
					subject="brand"
					defaultName={brandName}
					emptyLabel="This brand goes by the same name in every market."
				/>
				<BrandLinksCard links={links} />
			</div>
		</div>
	)
}
